import {
    forwardRef
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type ICarouselDotProps from "./type";
import type {
    CarouselDotWebPropsType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    getHitArea
} from "../../../../helpers/theme/control";
import StateLayer from "../../../stateLayer";

const CarouselDot = forwardRef<View, ICarouselDotProps>(({
    customTheme,
    orientation,
    isSelected,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onKeyDown,
    onPressIn,
    onFocus,
    onPress,
    onBlur,
    label,
    ...props
}, ref) => {
    const {
        touchTargets,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const hitArea = getHitArea({
        visualSize: borders.indicator * 2,
        touchTargets,
        pointerType
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle,
        dot: dotDynamicStyle
    } = useStyles({
        hitSize: hitArea.minHeight,
        isFocusVisible,
        orientation,
        isSelected,
        isHovered,
        borders,
        colors
    });

    const webProps: CarouselDotWebPropsType = Platform.OS === "web" ? {
        onKeyDown
    } : {};

    return <Pressable
        {...props}
        {...interactionHandlers}
        {...webProps}
        tabIndex={isSelected ? 0 : -1}
        aria-selected={isSelected}
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
        aria-label={label}
        onPress={onPress}
        role="tab"
        ref={ref}
    >
        <View
            style={[
                stylesheet.surface,
                isSelected ? stylesheet.selected : undefined,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                isPressed={!isSelected && isPressed}
                isHovered={!isSelected && isHovered}
                borderRadius={radiuses.full}
                customTheme={customTheme}
                overlayColor="default"
            />
            <View
                style={dotDynamicStyle}
            />
        </View>
    </Pressable>;
});

CarouselDot.displayName = "CarouselDot";
export default CarouselDot;
